import type { Price } from '@backmarket/http-api/src/api-models/Price'
import type {
  GetLegacyServicePickersResponse,
  GetPickersResponse,
} from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import { applyDealsToPickerGroup } from '@backmarket/nuxt-layer-deals/applyDealsToPickerGroup'
import type { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import { applyOperationOnPickers } from '~/scopes/product-core/components/presentationals/CustomizationFunnel/utils/applyOperationOnPickers'
import { applyUpgradeForLessTagGate } from '~/scopes/product-core/components/presentationals/CustomizationFunnel/utils/applyUpgradeForLessTagGate'
import { getMobilePlanStep } from '~/scopes/product-core/components/presentationals/CustomizationFunnel/utils/steps/mobilePlan'
import {
  getTradeInAUStep,
  getTradeInStep,
} from '~/scopes/product-core/components/presentationals/CustomizationFunnel/utils/steps/tradeIn'
import type { CondensedStep } from '~/scopes/product-core/components/presentationals/CustomizationFunnelCondensed/CustomizationFunnelCondensed.types'

import {
  type GetPickerDisplay,
  getCondensedTitle,
  mapCondensedStep,
  toCondensedStep,
} from './mapCondensedStep'

/**
 * This function maps the response of the existing endpoints (pickers,
 * services pickers) to the config object consumed by the condensed funnel.
 *
 * It mirrors `createCustomizationFunnelData` with the condensed experiment
 * deltas applied: no step images, no guidance card icons, good deal info
 * stripped, grades-only side content, and a picker display config per row.
 */
export const createCondensedFunnelData = (
  i18n: ReturnType<typeof useI18n>,
  pickers: GetPickersResponse,
  pickersServices: GetLegacyServicePickersResponse['pickerGroups'],
  product: GetProductResponse,
  price: Price | null | undefined,
  discountedPrice: Price,
  experiments: Record<`experiment.${string}`, string>,
  getPickerDisplay: GetPickerDisplay,
): CondensedStep[] => {
  const variantsSteps: CondensedStep[] = pickers.pickerGroups.map((group) => {
    let processedGroup = group

    if (experiments['experiment.flashSalesOnPDP'] === 'enabled') {
      processedGroup = applyDealsToPickerGroup(group, i18n)
    }

    return mapCondensedStep(product, processedGroup, getPickerDisplay)
  })

  const servicesSteps: CondensedStep[] = pickersServices
    .map((group) => {
      switch (group.id) {
        case 'mobile_plan': {
          const mobilePlanStep = getMobilePlanStep(i18n, group, price)

          return mobilePlanStep
            ? toCondensedStep(mobilePlanStep, getPickerDisplay)
            : null
        }
        case 'trade_in': {
          const tradeInStep = getTradeInStep(
            product,
            group,
            i18n,
            discountedPrice,
          )

          return {
            ...toCondensedStep(tradeInStep, getPickerDisplay),
            title: getCondensedTitle(group),
            showTelcoPickerBefore: true,
          }
        }
        default:
          return null
      }
    })
    // TS way of narrowing the type of the returned Array
    // to discard nullish values, while filtering out all
    // falsy values
    .filter((step): step is CondensedStep => Boolean(step))

  let steps: CondensedStep[] = [...variantsSteps, ...servicesSteps]

  // [ToDo CondensedFunnel] trade_in_au is temporarily re-gated on
  // experiment.tradeInAU until the trade-in team confirms the global
  // rollout (the classic funnel still gates on it; the config is
  // noVariation + urlPatterns /en-au/ only).
  if (experiments['experiment.tradeInAU'] === 'enabled') {
    steps.push(toCondensedStep(getTradeInAUStep(i18n), getPickerDisplay))
  }

  // All operations to be applied on pickers.
  // We iterate over service pickers to retrieve the operations associated to the selected ones.
  const operations = servicesSteps.map((step) => {
    const operation = step.options.find(({ selected }) => !!selected)
      ?.operation || {
      type: 'NoOperation' as const,
      price: null,
    }

    return {
      id: step?.id,
      operation,
    }
  })

  operations.forEach(({ id, operation }) => {
    steps = applyOperationOnPickers(steps, id, operation)
  })

  return steps.map((step) => ({
    ...step,
    options: applyUpgradeForLessTagGate(step.options, step.id, experiments),
  }))
}
